import { CPU_CORE_COUNT } from './browser/globalEnvironment';

// Every playing `<video>` holds a decode pipeline of its own, so a grid of them — the GIF picker, a
// media-heavy chat — can saturate the CPU while nothing on screen looks out of the ordinary. Playback
// past this budget is queued instead of started, and a freed slot goes to whoever has waited longest.
// Nothing already playing is ever evicted: pausing what the user is watching so that something else
// can start reads as a bug.
const VIDEOS_PER_CORE = 2;
const MIN_CONCURRENT_VIDEOS = 6;
const MAX_CONCURRENT_VIDEOS = 16;

const MAX_CONCURRENT = Math.min(
  Math.max(CPU_CORE_COUNT * VIDEOS_PER_CORE, MIN_CONCURRENT_VIDEOS),
  MAX_CONCURRENT_VIDEOS,
);

const playingVideos = new Set<HTMLVideoElement>();
// Insertion-ordered, so the first entry is the longest-waiting video
const waitingVideos = new Map<HTMLVideoElement, NoneToVoidFunction>();

// Returns whether `video` may start right now. When it may not, `startPlayback` is called instead as
// soon as a slot frees up, so a caller that gets `false` must not start playback on its own.
export function requestPlaybackSlot(video: HTMLVideoElement, startPlayback: NoneToVoidFunction) {
  waitingVideos.delete(video);

  if (playingVideos.has(video) || playingVideos.size < MAX_CONCURRENT) {
    playingVideos.add(video);
    return true;
  }

  waitingVideos.set(video, startPlayback);

  return false;
}

export function releasePlaybackSlot(video: HTMLVideoElement) {
  waitingVideos.delete(video);
  if (!playingVideos.delete(video)) return;

  promoteWaitingVideos();
}

function promoteWaitingVideos() {
  while (playingVideos.size < MAX_CONCURRENT && waitingVideos.size) {
    const [video, startPlayback] = waitingVideos.entries().next().value!;
    waitingVideos.delete(video);

    // Queued while mounted, unmounted before its turn came
    if (!video.isConnected) continue;

    // `startPlayback` claims the free slot through `requestPlaybackSlot` itself, and declines it if
    // the video no longer wants to play — claiming it here would strand the slot in that case
    startPlayback();
  }
}
